import { Routes } from '@/routes'
import { useStytchB2BClient, useStytchMember, useStytchMemberSession } from '@stytch/react/b2b'
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import type { FC, ReactNode } from 'react'

interface AuthContextType {
	userId?: string
	loaded: boolean
	loggedIn: boolean
	logout: () => Promise<void>
	login: () => void
	getToken: () => Promise<string | undefined>
	email?: string
}

const AuthContext = createContext({} as AuthContextType)

const AuthLoader: FC<{ children: React.ReactNode }> = ({ children }) => {
	const { loaded, loggedIn } = useAuth()
	const navigate = useNavigate()
	const location = useLocation()

	useEffect(() => {
		if (loaded && !loggedIn && location.pathname !== '/authenticate') {
			navigate('/authenticate')
		}
	}, [loaded, loggedIn, location.pathname, navigate])

	return children
}

export { AuthLoader }

export function AuthProvider({ children }: { children: ReactNode }) {
	const [loaded, setLoaded] = useState(false)
	const [loggedIn, setLoggedIn] = useState(false)
	const [userId, setUserId] = useState<string | undefined>()
	const [email, setEmail] = useState<string | undefined>()
	const navigate = useNavigate()
	const { session } = useStytchMemberSession()
	const { member } = useStytchMember()
	const client = useStytchB2BClient()

	useEffect(() => {
		if (session) {
			setLoggedIn(true)
			setUserId(session.member_id)
		}
		if (member) {
			setEmail(member.email_address)
		}
		setLoaded(true)
	}, [session, member])

	// Read Stytch session JWT cookie for API/WS auth
	const getToken = useMemo(() => {
		return async () => {
			const cookie = document.cookie
				.split('; ')
				.find((c) => c.startsWith('stytch_session_jwt='))
				?.split('=')[1]
			return cookie
		}
	}, [])

	function login() {
		navigate(Routes.Authenticate, { replace: true })
	}

	async function logout() {
		await client.session.revoke()
		navigate(Routes.Authenticate, { replace: true })
	}

	return (
		<AuthContext.Provider
			value={{
				login,
				logout,
				userId,
				loaded,
				loggedIn,
				getToken,
				email,
			}}
		>
			{children}
		</AuthContext.Provider>
	)
}

export function useAuth() {
	return useContext(AuthContext)
}
